import { useState, useEffect } from "react";
import {
  Bell,
  MessageCircle,
  DollarSign,
  UserPlus,
  FileText,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import api from "../../lib/api";
import { formatDistanceToNow } from "date-fns";

const iconMap = {
  message: MessageCircle,
  payment: DollarSign,
  team: UserPlus,
  request: FileText,
  job: CheckCircle,
  alert: AlertCircle,
  job_completed: CheckCircle,
  new_quote: FileText,
  comment: MessageCircle,
};

const colorMap = {
  message: "from-blue-500 to-blue-600",
  payment: "from-green-500 to-emerald-600",
  team: "from-purple-500 to-purple-600",
  request: "from-orange-500 to-orange-600",
  job: "from-cyan-500 to-cyan-600",
  alert: "from-red-500 to-rose-600",
  job_completed: "from-cyan-500 to-cyan-600",
  new_quote: "from-orange-500 to-orange-600",
  comment: "from-pink-500 to-pink-600",
};

export default function NotificationPage() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  useEffect(() => {
    const fetchNotifications = async () => {
      setLoading(true);
      try {
        const res = await api.get(`/company/notifications?page=${page}`);
        const newData = res.data.data || [];
        const pagination = res.data.pagination || {};

        if (page === 1) {
          setNotifications(newData);
        } else {
          setNotifications((prev) => [...prev, ...newData]);
        }

        setUnreadCount(res.data.unread_count || 0);
        setHasMore(pagination.current_page < pagination.last_page);
      } catch (err) {
        console.error("Failed to load notifications", err);
      } finally {
        setLoading(false);
      }
    };

    fetchNotifications();
  }, [page]);

  const markAsRead = async (id) => {
    try {
      await api.put(`/company/notifications/${id}/read`);
      setNotifications((prev) =>
        prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)),
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (err) {
      console.error("Failed to mark as read", err);
    }
  };

  const markAllAsRead = async () => {
    try {
      await api.put("/company/notifications/read-all");
      setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      setUnreadCount(0);
    } catch (err) {
      console.error("Failed to mark all as read", err);
    }
  };

  return (
    <section className="min-h-screen py-8">
      <div>
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            All Notifications
          </h1>
          {unreadCount > 0 && (
            <button
              onClick={markAllAsRead}
              className="text-sm px-4 py-2 bg-blue-50 text-blue-600 font-medium rounded-lg hover:bg-blue-100 transition-colors"
            >
              Mark all as read
            </button>
          )}
        </div>

        {unreadCount > 0 && (
          <div className="mb-6 text-lg text-gray-700">
            You have{" "}
            <span className="font-bold text-red-600">{unreadCount}</span> unread
            notifications
          </div>
        )}

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
          {notifications.length === 0 && !loading ? (
            <div className="p-12 text-center text-gray-500">
              No notifications yet
            </div>
          ) : (
            <>
              {notifications.map((notif) => {
                const Icon = iconMap[notif.type] || Bell;
                const color =
                  colorMap[notif.type] || "from-gray-500 to-gray-600";

                return (
                  <div
                    key={notif.id}
                    className={`flex items-start gap-4 p-6 border-b border-gray-100 last:border-0 ${
                      !notif.is_read ? "bg-blue-50/30" : ""
                    }`}
                  >
                    <div
                      className={`w-12 h-12 rounded-xl bg-gradient-to-br ${color} flex items-center justify-center shadow-md flex-shrink-0`}
                    >
                      <Icon className="w-6 h-6 text-white" />
                    </div>

                    <div className="flex-1">
                      <h4 className="font-semibold text-gray-900">
                        {notif.title}
                      </h4>
                      <p className="text-sm text-gray-600 mt-1">
                        {notif.message}
                      </p>
                      <p className="text-xs text-gray-500 mt-2">
                        {formatDistanceToNow(new Date(notif.created_at), {
                          addSuffix: true,
                        })}
                      </p>
                    </div>

                    {!notif.is_read && (
                      <button
                        onClick={() => markAsRead(notif.id)}
                        className="text-xs text-blue-600 hover:text-blue-700 font-medium"
                      >
                        Mark as read
                      </button>
                    )}
                  </div>
                );
              })}

              {loading && (
                <div className="p-8 text-center text-gray-500">
                  Loading notifications...
                </div>
              )}

              {hasMore && !loading && (
                <div className="p-4 text-center">
                  <button
                    onClick={() => setPage((prev) => prev + 1)}
                    className="text-sm font-medium text-blue-600 hover:text-blue-700"
                  >
                    Load more
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </section>
  );
}
